"use client";

/**
 * Shared PortableText component factory for next-sanity.
 *
 * This module lives in the UI package because it only needs Typography and class names:
 * it renders standard block content (paragraphs, headings), lists (bullet/number), and
 * link marks (including optional open-in-new-tab) in a schema-agnostic way. Any app can
 * call getPortableTextComponents() with its own CSS class names and get consistent
 * block/list/link rendering.
 *
 * Custom block types (e.g. image, button) are NOT implemented here because they depend on:
 * - App or Sanity schema (block shape, asset refs)
 * - Resolved data (e.g. imageDataMap with URLs/dimensions from Sanity)
 * - Next.js Image or other app-specific components
 * Apps that need them pass options.types when calling getPortableTextComponents().
 */
import { c as _c } from "react/compiler-runtime";
import { isValidElement, type ComponentProps, type ReactNode } from "react";
import { PortableText } from "next-sanity";
import { cmsTypographyVariantKeys, Typography, type TypographyVariantName, typographyVariants } from "@repo/ui/aops-components/typography";
import { cn } from "@repo/ui/lib/utils";
import styles from "./portable-text-components.module.css";
export type PortableTextClassNames = {
  paragraph?: string;
  heading?: string;
  list?: string;
  listItem?: string;
  listNumber?: string;
  listItemNumber?: string;
};

/** Legacy Portable Text block styles (standard h1–h6 + normal) → PACT variants. */
const legacyPortableTextBlockVariants = {
  normal: "body",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h4",
  h6: "h4"
} as const satisfies Record<string, TypographyVariantName>;
function isHeadingTypographyVariant(variant: TypographyVariantName): boolean {
  return variant === "h1" || variant === "h2" || variant === "h3" || variant === "h4" || variant === "stat" || variant.startsWith("display");
}
function getBlockClassName(variant: TypographyVariantName, classNames: {
  paragraph: string;
  heading: string;
}): string {
  if (isHeadingTypographyVariant(variant)) {
    return classNames.heading;
  }
  return classNames.paragraph;
}
function getTextFromReactNode(node: ReactNode): string {
  if (node == null || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromReactNode).join("");
  }
  if (isValidElement(node)) {
    const props = node.props as {
      children?: ReactNode;
    };
    return getTextFromReactNode(props.children);
  }
  return "";
}

/** Converts heading text into a URL fragment id (e.g. "Hello World" → "hello-world"). */
function toKebabCaseId(text: string): string | undefined {
  const id = text.trim().toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
  return id || undefined;
}
function createBlockHandler(variant: TypographyVariantName, className: string): ({
  children
}: {
  children?: ReactNode;
}) => React.ReactElement {
  const BlockComponent = t0 => {
    const $ = _c(6);
    const {
      children
    } = t0;
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = isHeadingTypographyVariant(variant);
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    const isHeading = t1;
    let t2;
    if ($[1] !== children) {
      t2 = isHeading ? toKebabCaseId(getTextFromReactNode(children)) : undefined;
      $[1] = children;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const id = t2;
    let t3;
    if ($[3] !== children || $[4] !== id) {
      t3 = <Typography id={id} variant={variant} className={className}>{children}</Typography>;
      $[3] = children;
      $[4] = id;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  };
  BlockComponent.displayName = `BlockHandler(${variant})`;
  return BlockComponent;
}
function createPortableTextBlockHandlers(classNames: {
  paragraph: string;
  heading: string;
}): Record<string, ({
  children
}: {
  children?: ReactNode;
}) => React.ReactElement> {
  const handlers: Record<string, ({
    children
  }: {
    children?: ReactNode;
  }) => React.ReactElement> = {};
  for (const [style, variant] of Object.entries(legacyPortableTextBlockVariants)) {
    handlers[style] = createBlockHandler(variant, getBlockClassName(variant, classNames));
  }
  for (const variant of cmsTypographyVariantKeys) {
    handlers[variant] = createBlockHandler(variant, getBlockClassName(variant, classNames));
  }
  return handlers;
}

/** Props passed by next-sanity to custom block type components. We only use value; rest are for compatibility. */
export type PortableTextTypeComponentProps = {
  value: unknown;
  index?: number;
  isInline?: boolean;
  renderNode?: unknown;
};

/** Type for custom block types (e.g. image, button). Callers merge these via options.types. */
export type PortableTextCustomTypes = Record<string, React.ComponentType<PortableTextTypeComponentProps>>;
export type PortableTextLinkMarkValue = {
  href?: string;
  openInNewTab?: boolean;
};
export type GetPortableTextComponentsOptions = {
  classNames: PortableTextClassNames;
  types?: PortableTextCustomTypes;
};
function LinkMark(t0) {
  const $ = _c(8);
  const {
    children,
    value
  } = t0;
  const href = value?.href;
  if (!href) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const openInNewTab = value?.openInNewTab === true;
  let t1;
  if ($[2] !== openInNewTab) {
    t1 = openInNewTab ? {
      target: "_blank",
      rel: "noopener noreferrer"
    } : undefined;
    $[2] = openInNewTab;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== href || $[6] !== t1) {
    t2 = <a href={href} {...t1}>{children}</a>;
    $[4] = children;
    $[5] = href;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
interface TableCell {
  _key?: string;
  value: unknown[];
}
interface TableRow {
  _key?: string;
  cells: TableCell[];
}
interface TableValue {
  rows: TableRow[];
  headerRows?: number;
}
function isTableCell(cell: unknown): cell is TableCell {
  return typeof cell === "object" && cell !== null && "value" in cell && Array.isArray(cell.value);
}
function isTableRow(row: unknown): row is TableRow {
  return typeof row === "object" && row !== null && "cells" in row && Array.isArray(row.cells) && row.cells.every(isTableCell);
}
function isTableValue(value: unknown): value is TableValue {
  return typeof value === "object" && value !== null && "rows" in value && Array.isArray(value.rows) && value.rows.every(isTableRow);
}
type NestedPortableTextComponents = ComponentProps<typeof PortableText>["components"];
function createPortableTextTable(cellComponents: NestedPortableTextComponents) {
  function PortableTextTable(t0) {
    const $ = _c(13);
    const {
      value
    } = t0;
    if (!isTableValue(value) || value.rows.length === 0) {
      return null;
    }
    const headerCount = typeof value.headerRows === "number" ? Math.max(0, Math.min(value.headerRows, value.rows.length)) : 0;
    let t1;
    let t2;
    let t3;
    let t4;
    if ($[0] !== headerCount || $[1] !== value.rows) {
      const headerRows = value.rows.slice(0, headerCount);
      const bodyRows = value.rows.slice(headerCount);
      const renderCellContent = _temp;
      t4 = styles.tableWrapper;
      t1 = styles.table;
      t2 = headerRows.length > 0 && <thead>{headerRows.map((row, rowIndex) => <tr key={row._key ?? rowIndex}>{row.cells.map((cell_0, cellIndex) => <th key={cell_0._key ?? cellIndex}>{renderCellContent(cell_0)}</th>)}</tr>)}</thead>;
      t3 = bodyRows.length > 0 && <tbody>{bodyRows.map((row_0, rowIndex_0) => <tr key={row_0._key ?? rowIndex_0}>{row_0.cells.map((cell_1, cellIndex_0) => <td key={cell_1._key ?? cellIndex_0}>{renderCellContent(cell_1)}</td>)}</tr>)}</tbody>;
      $[0] = headerCount;
      $[1] = value.rows;
      $[2] = t1;
      $[3] = t2;
      $[4] = t3;
      $[5] = t4;
    } else {
      t1 = $[2];
      t2 = $[3];
      t3 = $[4];
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
      t5 = <table className={t1}>{t2}{t3}</table>;
      $[6] = t1;
      $[7] = t2;
      $[8] = t3;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = <div className={t4}>{t5}</div>;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  function _temp(cell) {
    return cell.value.length > 0 && <PortableText value={cell.value as ComponentProps<typeof PortableText>["value"]} components={cellComponents} />;
  }
  PortableTextTable.displayName = "PortableTextTable";
  return PortableTextTable;
}

/**
 * Builds PortableText components (block, list, listItem, marks) for use with
 * next-sanity's <PortableText components={...} />. Pass class names from your
 * app's CSS module. Optional types (e.g. image, button) are provided by the app
 * and merged in here—the UI package does not define them, since they depend on
 * schema and resolved data.
 */
export function getPortableTextComponents(options: GetPortableTextComponentsOptions): {
  block: Record<string, ({
    children
  }: {
    children?: ReactNode;
  }) => React.ReactElement>;
  list: {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
  };
  listItem: {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
  };
  marks: {
    link: typeof LinkMark;
  };
  types: PortableTextCustomTypes;
} {
  const {
    paragraph = "",
    heading = "",
    list = "",
    listItem = "",
    listNumber,
    listItemNumber
  } = options.classNames;
  const listNum = listNumber ?? list;
  const listItemNum = listItemNumber ?? listItem;
  const block = createPortableTextBlockHandlers({
    paragraph,
    heading
  });
  const listComponents = {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => <ul className={list}>{children}</ul>,
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => <ol className={listNum}>{children}</ol>
  };
  const listItemComponents = {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => <li className={cn(typographyVariants({
      variant: "body"
    }), listItem)}>
				{children}
			</li>,
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => <li className={cn(typographyVariants({
      variant: "body"
    }), listItemNum)}>
				{children}
			</li>
  };
  const result: ReturnType<typeof getPortableTextComponents> = {
    block,
    list: listComponents,
    listItem: listItemComponents,
    marks: {
      link: LinkMark
    },
    types: {
      table: createPortableTextTable({
        block,
        list: listComponents,
        listItem: listItemComponents,
        marks: {
          link: LinkMark
        }
      })
    }
  };
  if (options.types && Object.keys(options.types).length > 0) {
    Object.assign(result.types, options.types);
  }
  return result;
}